import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Card from '../common/Card';
import Button from '../common/Button';
import Dropdown from '../ui/Dropdown';
import { useAuth } from '../../contexts/AuthContext';
import HolidayService from '../../services/holiday.service';
import toast from '../../utils/toast';
import { formatDateDisplay } from '../../utils/dateDisplay';

const initialForm = {
  name: '',
  date: '',
  description: '',
  type: 'public'
};

const resolveDbId = (holiday) => {
  if (holiday?.databaseId != null) return holiday.databaseId;
  const s = String(holiday?.id || '');
  const m = s.match(/^db-(\d+)$/);
  return m ? Number(m[1]) : null;
};

const HolidayAdminPanel = ({ compact = false }) => {
  const { isAdmin, isHR } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [counts, setCounts] = useState({ total: 0, approved: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState(initialForm);

  const canManage = isAdmin || isHR;

  const fetchHolidays = async () => {
    if (!canManage) return;

    try {
      setLoading(true);
      const response = await HolidayService.getMergedHolidays(new Date().getFullYear());
      setHolidays(response?.holidays || []);
      setCounts(response?.counts || { total: 0, approved: 0, pending: 0 });
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error(error.response?.data?.message || 'Failed to load holidays');
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [canManage]);

  const filteredHolidays = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return holidays.filter((holiday) => {
      const matchesSearch = !query
        || (holiday.name || '').toLowerCase().includes(query)
        || (holiday.description || '').toLowerCase().includes(query);
      const matchesType = !typeFilter || holiday.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [holidays, searchTerm, typeFilter]);

  const openCreateForm = () => {
    setEditingHoliday(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const openEditForm = (holiday) => {
    setEditingHoliday(holiday);
    setForm({
      name: holiday.name || '',
      date: holiday.date ? String(holiday.date).split('T')[0] : '',
      description: holiday.description || '',
      type: holiday.type || 'public'
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingHoliday(null);
    setForm(initialForm);
  };

  const saveHoliday = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.date) {
      toast.error('Holiday name and date are required');
      return;
    }

    const payload = {
      name: form.name.trim(),
      date: form.date,
      description: form.description.trim(),
      type: form.type
    };

    try {
      if (editingHoliday) {
        const pk = resolveDbId(editingHoliday);
        if (pk == null) {
          toast.error('Could not resolve holiday id');
          return;
        }
        await HolidayService.updateHoliday(pk, payload);
        toast.success('Holiday updated successfully');
      } else {
        await HolidayService.createHoliday(payload);
        toast.success('Holiday created successfully');
      }
      closeForm();
      fetchHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
      toast.error(error.response?.data?.message || 'Failed to save holiday');
    }
  };

  const deleteHoliday = async (holiday) => {
    const pk = resolveDbId(holiday);
    if (pk == null) {
      toast.error('Could not resolve holiday id');
      return;
    }
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await HolidayService.deleteHoliday(pk);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error(error.response?.data?.message || 'Failed to delete holiday');
    }
  };

  const approveHoliday = async (holiday) => {
    const pk = resolveDbId(holiday);
    if (pk == null) {
      toast.error('Could not resolve holiday id');
      return;
    }
    try {
      await HolidayService.approveHoliday(pk);
      toast.success('Approved as company holiday');
      fetchHolidays();
    } catch (error) {
      console.error('Error approving holiday:', error);
      toast.error(error.response?.data?.message || 'Failed to approve');
    }
  };

  if (!canManage) return null;

  return (
    <div className="space-y-4">
      <Card>
        <Card.Body className="p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1 min-w-0">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search holiday..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                />
              </div>
              <Dropdown
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { value: '', label: 'All types' },
                  { value: 'public', label: 'Public' },
                  { value: 'optional', label: 'Optional' },
                  { value: 'company', label: 'Company' }
                ]}
                className="w-full sm:w-[180px]"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {!compact && (
                <>
                  <div className="text-right">
                    <p className="text-xl font-semibold text-gray-900">{counts.approved ?? holidays.filter((h) => h.isCompanyApproved).length}</p>
                    <p className="text-xs text-gray-500">Company holidays</p>
                  </div>
                  <div className="rounded-lg border border-amber-100 bg-amber-50/80 px-3 py-2 text-right">
                    <p className="text-xl font-semibold text-amber-900">{counts.pending ?? holidays.filter((h) => !h.isCompanyApproved).length}</p>
                    <p className="text-xs text-amber-800">Pending approval</p>
                  </div>
                </>
              )}
              <Button onClick={openCreateForm} icon={PlusIcon}>Add Holiday</Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <Card>
          <Card.Body className="p-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="h-20 animate-pulse rounded-lg bg-gray-100" />
              ))}
            </div>
          </Card.Body>
        </Card>
      ) : filteredHolidays.length === 0 ? (
        <Card>
          <Card.Body className="p-10 text-center">
            <CalendarIcon className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <p className="font-medium text-gray-800">No matching holidays</p>
            <p className="mt-1 text-sm text-gray-500">Change filters or add a new holiday.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body className="p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {filteredHolidays.map((holiday) => {
                const pending = !holiday.isCompanyApproved;
                return (
                  <div
                    key={holiday.id}
                    className={`rounded-lg border bg-white p-3 ${pending ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900">{holiday.name}</p>
                        <p className="text-xs text-gray-600">
                          {formatDateDisplay(holiday.date)}
                        </p>
                        {pending ? (
                          <p className="mt-1 text-[11px] font-medium text-amber-800">
                            Suggested — not on company calendar until approved
                            {holiday.source === 'api' ? ' (public holiday feed)' : ''}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-1">
                        {pending ? (
                          <button
                            type="button"
                            onClick={() => approveHoliday(holiday)}
                            className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                          >
                            Approve
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => openEditForm(holiday)}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-blue-600"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteHoliday(holiday)}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {pending ? (
                          <button
                            type="button"
                            onClick={() => deleteHoliday(holiday)}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                            title="Dismiss suggestion"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {holiday.description && (
                      <p className="mt-2 text-xs text-gray-600">{holiday.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeForm}
          >
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
                </h3>
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <form className="space-y-4 p-5" onSubmit={saveHoliday}>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Holiday name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
                  <Dropdown
                    value={form.type}
                    onChange={(value) => setForm((prev) => ({ ...prev, type: value }))}
                    options={[
                      { value: 'public', label: 'Public Holiday' },
                      { value: 'optional', label: 'Optional Holiday' },
                      { value: 'company', label: 'Company Holiday' }
                    ]}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                    rows={3}
                  />
                </div>

                <div className="flex gap-3 pt-1">
                  <Button type="submit" className="flex-1">
                    {editingHoliday ? 'Save changes' : 'Create holiday'}
                  </Button>
                  <Button type="button" variant="outline" className="flex-1" onClick={closeForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HolidayAdminPanel;
